import { useQuery } from "@tanstack/react-query";
import { Button, Col, Flex, Form, Input, InputNumber, List, Popconfirm, Row, Select, Tag, Typography } from "antd";
import { useForm, useWatch } from "antd/es/form/Form";
import React, { SetStateAction, useEffect, useMemo, useState } from "react"
import InputMask from "react-input-mask";

import { EnderecoService } from "../../Services/EnderecoService";
import { EditOutlined } from "@ant-design/icons";
import { EnumEnderecoTipo, TypeEndereco } from "../../Models/Pessoa/Endereco";

import { Regex } from "../../Constants";
import { Formatacoes } from "../../Utils/Formatacao";
import { SelectComPesquisa } from "../SelectComPesquisa";
import Ordenar from "@/Utils/Ordenacao";

interface IFormularioDeEnderecos {
    enderecosAPI: [ TypeEndereco[], React.Dispatch<SetStateAction<TypeEndereco[]>> ],

    distancia?: boolean,
    permitirEdicaoDosEnderecos?: boolean,
}

export const FormularioDeEnderecos: React.FC<IFormularioDeEnderecos> = (props) => {

    const [enderecos, setEnderecos] = props.enderecosAPI;

    interface IFormulario extends TypeEndereco { }

    const enderecoService = new EnderecoService();

    const [enderecoEmEdicao, setEnderecoEmEdicao] = useState<TypeEndereco | undefined>(undefined);

    const [IDDeAdicao, setIDDeAdicao] = useState<number>(-1);
    const [enderecoRecebidoDoViaCEP, setEnderecoRecebidoDoViaCEP] = useState<Partial<TypeEndereco>>({})

    const [form] = useForm<IFormulario>();
    const estadoSelecionado = useWatch(["estado", "codigo"], form);

    const estaEditando = useMemo(() => !!enderecoEmEdicao, [enderecoEmEdicao]);

    const tiposDeEnderecoDisponiveis = useQuery({
        queryKey: ["listarTipoEnderecos"],
        queryFn: () => enderecoService.listarTiposEndereco()
    });

    const estadosDisponiveis = useQuery({
        queryKey: ["listarEstados"],
        queryFn: () => enderecoService.listarEstados().then((r) => {
            r.data.sort((a, b) => Ordenar.String(a.nome, b.nome))
            return r;
        })
    });

    const municipiosDisponiveis = useQuery({
        queryKey: ["listarMunicipios", estadoSelecionado],
        queryFn: () => enderecoService.listarMunicipios({ codigo: estadoSelecionado }).then((r) => {
            r.data.sort((a, b) => Ordenar.String(a.nome, b.nome))
            return r;
        })
    });

    const onFinish = (valores: IFormulario) => {
        valores.ativo = true;

        valores.estado.nome = estadosDisponiveis.data?.data.find((e) => e.codigo == valores.estado.codigo)?.nome
        valores.municipio.nome = municipiosDisponiveis.data?.data.find((m) => m.codigo == valores.municipio.codigo)?.nome
        valores.tipo.tipo = tiposDeEnderecoDisponiveis.data?.data.find((k) => k.codigo == valores.tipo.codigo)?.tipo

        if (estaEditando) {
            if(!enderecoEmEdicao?.codigo) throw new Error();

            valores.codigo = enderecoEmEdicao?.codigo;
            setEnderecos((prev) => prev.map((p) => p.codigo == enderecoEmEdicao?.codigo ? valores : p));
            setEnderecoEmEdicao(undefined)
        } else {
            valores.codigo = IDDeAdicao;
            setIDDeAdicao(IDDeAdicao - 1)
            setEnderecos((prev) => [...prev, valores]);
        }

        form.resetFields();
        setEnderecoRecebidoDoViaCEP({});
    };

    useEffect(() => {
        tiposDeEnderecoDisponiveis.refetch();
    }, [enderecos]);

    return (
        <React.Fragment>
            {estaEditando && (
                <Row gutter={16}>
                    <Col xs={24}>
                        {(() => {
                            const end = enderecos.find((v) => v.codigo == enderecoEmEdicao?.codigo);
                            if (!end) return;

                            return (
                                <Typography.Paragraph>
                                    <Tag color="geekblue-inverse">
                                        {" "}
                                        <EditOutlined /> {end?.logradouro}, {end.numero}
                                    </Tag>
                                </Typography.Paragraph>
                            );
                        })()}
                    </Col>
                </Row>
            )}

            <Form form={form} onFinish={onFinish} autoComplete="off" layout="vertical">
                <Row gutter={16}>
                    <Col xs={24} md={4}>
                        <Form.Item<IFormulario>
                            name={["tipo", "codigo"]}
                            rules={[{ required: true, message: "Por favor, informe o tipo!" }]}
                            label="Tipo de Endereço"
                        >
                            <Select
                                loading={tiposDeEnderecoDisponiveis.isLoading && tiposDeEnderecoDisponiveis.isFetching}
                                disabled={tiposDeEnderecoDisponiveis.isError}
                                options={tiposDeEnderecoDisponiveis.data?.data
                                    .filter((t) =>
                                        /* Só pode ser cadastrado um endereço para cada tipo, com exceção dos endereços de entrega. */
                                        t.codigo === EnumEnderecoTipo.ENTREGA
                                            ? true :  
                                            (enderecoEmEdicao && enderecoEmEdicao?.tipo.codigo == t.codigo) ||
                                            enderecos.findIndex((e) => e.tipo.codigo == t.codigo) == -1
                                    )
                                    .map((item) => {
                                        return {
                                            value: item.codigo,
                                            label: item.tipo,
                                        };
                                    })}
                                placeholder={"Selecione"}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={4}>
                        <Form.Item<IFormulario>
                            name={["cep"]}
                            validateTrigger="onBlur"
                            label="CEP"
                            rules={[
                                {
                                    required: true,
                                    pattern: Regex.CEP,
                                    validator: async (rules, val: string) => {
                                        setEnderecoRecebidoDoViaCEP({});

                                        if (val == undefined) return Promise.reject("O CEP é obrigatório!");

                                        if (!val.match(rules.pattern!)) return Promise.reject("Insira um CEP no formato válido!");
                                        const cep = Formatacoes.sanitizar("-_", val);

                                        try {
                                            const enderecoEncontrado = await enderecoService
                                                .obterInformacoesDoCEP(cep!)
                                                .then((info) => Formatacoes.viaCepParaEndereco(info, estadosDisponiveis.data?.data));
                                            const _tmp: any = { ...enderecoEncontrado };
                                            Object.entries(_tmp).forEach(([k, v]) => {
                                                // essa modificação é necessária para não substituir os campos já existentes no formulário
                                                if (v === null || v === undefined || v === "") delete _tmp[k];
                                            });

                                            form.setFieldsValue(_tmp);
                                            setEnderecoRecebidoDoViaCEP(enderecoEncontrado);
                                        } catch (error) {
                                            return Promise.reject(error);
                                        }
                                    },
                                },
                            ]}
                        >
                            <InputMask mask="99999-999">
                                <Input placeholder="000.000-00" />
                            </InputMask>
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item<IFormulario>
                            name={["logradouro"]}
                            rules={[{ required: true, message: "Por favor, informe o logradouro!" }]}
                            label="Logradouro"
                        >
                            <Input disabled={enderecoRecebidoDoViaCEP.logradouro != null} placeholder="Informe o logradouro" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={4}>
                        <Form.Item<IFormulario> name={["numero"]} label="Número">
                            <Input placeholder="Informe o número" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col xs={24} md={6}>
                        <Form.Item<IFormulario> name={["bairro"]} rules={[{ required: true, message: "Por favor, informe o bairro!" }]} label="Bairro">
                            <Input disabled={enderecoRecebidoDoViaCEP.bairro != null} placeholder="Informe o bairro" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={3}>
                        <Form.Item<IFormulario>
                            name={["estado", "codigo"]}
                            rules={[{ required: true, message: "Por favor, informe o estado!" }]}
                            label="Estado"
                        >
                            <Select
                                loading={estadosDisponiveis.isLoading && estadosDisponiveis.isFetching}
                                disabled={estadosDisponiveis.isError || enderecoRecebidoDoViaCEP.estado?.codigo != null}
                                options={estadosDisponiveis.data?.data.map((item) => ({
                                    value: item.codigo,
                                    label: item.nome,
                                    sigla: item.sigla,
                                }))}
                                onChange={(v) => form.setFieldValue("estado", { codigo: v })}
                                placeholder="Selecione"
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={6}>
                        <Form.Item<IFormulario>
                            name={["municipio", "codigo"]}
                            rules={[{ required: true, message: "Por favor, informe o município!" }]}
                            label="Município"
                        >
                            <SelectComPesquisa
                                allowClear
                                loading={municipiosDisponiveis.isLoading && municipiosDisponiveis.isFetching}
                                disabled={municipiosDisponiveis.isError || estadoSelecionado == undefined || enderecoRecebidoDoViaCEP.municipio?.codigo != null}
                                options={municipiosDisponiveis.data?.data.map((item) => ({
                                    value: item.codigo,
                                    label: item.nome,
                                }))}
                                placeholder={estadoSelecionado == undefined ? "Selecione um estado para continuar" : "Selecione"}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={6}>
                        <Form.Item<IFormulario> name={["complemento"]} label="Complemento">
                            <Input placeholder="Informe o complemento" />
                        </Form.Item>
                    </Col>

                    {!!props.distancia && (
                        <Col xs={24} md={3}>
                            <Form.Item<IFormulario> name={["distancia"]} label="Distância">
                                <InputNumber name="distancia" min={0} precision={2} addonAfter="KM" />
                            </Form.Item>
                        </Col>
                    )}
                </Row>

                <Row>
                    <Col xs={24}>
                        <div style={{ display: "flex", justifyContent: "center", gap: 16 }}>
                            <Button type="primary" key="submit" htmlType="submit">
                                {estaEditando ? "Salvar" : "Incluir Endereço"}
                            </Button>

                            {estaEditando && (
                                <Button
                                    type="default"
                                    danger
                                    onClick={() => {
                                        form.resetFields();
                                        setEnderecoEmEdicao(undefined)
                                    }}
                                >
                                    Cancelar
                                </Button>
                            )}
                        </div>
                    </Col>
                </Row>
            </Form>

            <Row>
                <Col xs={24}>
                    <div style={{ paddingTop: 16 }}>
                        <List
                            header={<div className="common-list-header">Endereços adicionados</div>}
                            bordered
                            locale={{ emptyText: "Nenhum endereço encontrado." }}
                            dataSource={enderecos.filter((f) => f.ativo !== false)}
                            renderItem={(item) => (
                                <List.Item
                                    className="common-list-item"
                                    actions={(() => {
                                        const editar = (
                                            <Button
                                                disabled={estaEditando}
                                                type="primary"
                                                size="small"
                                                onClick={() => {
                                                    if (item.codigo == null) return;
                                                    setEnderecoEmEdicao(item);
                                                    form.setFieldsValue(item);
                                                }}
                                            >
                                                Editar
                                            </Button>
                                        );

                                        const excluir = (
                                            <Popconfirm
                                                title="Atenção"
                                                description="Tem certeza que deseja excluir este endereço?"
                                                okText="Confirmar"
                                                cancelText="Cancelar"
                                                onConfirm={() => {
                                                    setEnderecos((prev) => prev.map((v) => (v.codigo == item.codigo ? { ...v, ativo: false } : v)));
                                                }}
                                            >
                                                <Button disabled={estaEditando} type="default" danger size="small">
                                                    Excluir
                                                </Button>
                                            </Popconfirm>
                                        );

                                        if (props.permitirEdicaoDosEnderecos !== false) {
                                            return [editar, excluir];
                                        }

                                        return [excluir];
                                    })()}
                                >
                                    <Row className="common-list-item-render">
                                        <Col className="tag-holder" xs={24} xl={2}>
                                            <Tag color="blue">{item.tipo.tipo}</Tag>
                                        </Col>

                                        <Col>
                                            <Flex vertical>
                                                {`
                                            ${item.logradouro}, 
                                            ${item.numero ? item.numero + " - " : ""} 
                                            ${item.bairro} - 
                                            ${item.municipio?.nome ?? ''} - 
                                            ${item.estado?.nome ?? ''}, 
                                            ${item.cep} 
                                            ${item.complemento ? " - " + item.complemento : ""}`}
                                            </Flex>
                                        </Col>
                                    </Row>
                                </List.Item>
                            )}
                        />
                    </div>
                </Col>
            </Row>
        </React.Fragment>
    );

}